import React, { forwardRef } from 'react';
import { styled } from '@mui/material/styles';
import { Button, Menu, MenuItem, Theme, Typography } from '@mui/material';

const TextButton = styled(Button)`
  min-width: 0;
  padding: 0;
  color: #6c6c6c;
  text-transform: none;

  &:hover {
    background-color: transparent;
  }
`;

interface IContextButton {
  menuOpen: boolean;
  handleOpenMenu: () => void;
  children: JSX.Element | string;
  testId?: string;
  disabled?: boolean;
}

const ContextButton = forwardRef<HTMLButtonElement, IContextButton>(
  ({ menuOpen, handleOpenMenu, children, testId, disabled = false }, ref) => (
    <TextButton
      ref={ref}
      data-testid={testId}
      aria-haspopup='true'
      aria-expanded={menuOpen ? 'true' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        handleOpenMenu();
      }}
      type='button'
      disabled={disabled}
    >
      {children}
    </TextButton>
  ),
);

interface IContextMenu {
  menuOpen: boolean;
  anchorEl: HTMLElement | HTMLButtonElement | null;
  handleCloseMenu: () => void;
  contextActions: {
    name: string;
    href?: string;
    callback?: () => void;
    disabled?: boolean;
  }[];
  hoverColor?: (theme: Theme) => string;
}

const ContextMenu: React.FC<IContextMenu> = ({ menuOpen, anchorEl, handleCloseMenu, contextActions, hoverColor }) => {
  const handleItemClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>, callback?: () => void) => {
    e.stopPropagation();
    callback && callback();
    handleCloseMenu();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      MenuListProps={{
        'aria-labelledby': 'contextMenu-button',
        sx: {
          '& .MuiMenuItem-root': {
            width: 'auto',
            textAlign: 'left',
            '& a, p': {
              width: '100%',
              color: '#000000',
              textDecoration: 'none',
            },
            '&:hover': { backgroundColor: hoverColor || ((theme) => theme.palette.primary.light) },
          },
        },
      }}
    >
      {contextActions.map((action) => (
        <MenuItem key={action.name} onClick={(e) => handleItemClick(e, action.callback)} disabled={action.disabled}>
          {action.href ? <a href={action.href}>{action.name}</a> : <Typography>{action.name}</Typography>}
        </MenuItem>
      ))}
    </Menu>
  );
};

ContextButton.displayName = 'ContextButton';
export { ContextButton };
export default ContextMenu;
